import React from 'react';
import styled, { keyframes } from 'styled-components';
import { normalizePidStatus, pidStatusColor } from '../utils/pidStatus';

// Pulsing dot + status-name label for the read-only Package Index browse. Marks a
// non-Active card/panel (today only "Incoming" is surfaced). Active renders nothing —
// callers can mount <StatusIndicator status={...} /> unconditionally and the "Active
// shows no indicator" rule lives here.
//
// Colour comes from the shared pidStatus tokens (var(--color-status-*)), never raw
// literals (per the colorway rule). The pulse ring reuses the same token and fades via
// opacity + scale — no alpha literal needed — and is disabled under prefers-reduced-motion.

const pulse = keyframes`
  0%   { transform: scale(1);   opacity: 0.55; }
  100% { transform: scale(2.6); opacity: 0; }
`;

const Wrap = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: ${({ $size }) => ($size === 'sm' ? '0.72rem' : '0.8rem')};
  font-weight: 700;
  line-height: 1;
  color: var(--color-text);
  white-space: nowrap;
`;

const Dot = styled.span`
  position: relative;
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 50%;
  background: ${({ $bg }) => $bg};
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: ${({ $bg }) => $bg};
    animation: ${pulse} 1.6s ease-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after { animation: none; }
  }
`;

const StatusIndicator = ({ status, size, className }) => {
  const s = normalizePidStatus(status);
  if (s === 'Active') return null;
  const { bg } = pidStatusColor(s);
  return (
    <Wrap $size={size} className={className} title={s}>
      <Dot $bg={bg} aria-hidden="true" />
      <span>{s}</span>
    </Wrap>
  );
};

export default StatusIndicator;
